import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";
import { scrollToId } from "@/lib/lenis";

const HERO_IMG =
  "https://images.unsplash.com/photo-1774854445980-da96f04d1bb9?crop=entropy&cs=srgb&fm=jpg&q=85";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { delay: 0.25 + i * 0.16, duration: 1, ease: [0.16, 1, 0.3, 1] },
});

export const Hero = () => {
  const { t } = useLang();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 16 });
  const sry = useSpring(ry, { stiffness: 120, damping: 16 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={sectionRef}
      onMouseMove={onMouseMove}
      onMouseLeave={() => {
        rx.set(0);
        ry.set(0);
      }}
      className="relative flex min-h-screen flex-col overflow-hidden bg-[#FBF8F3]"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Architecture suisse contemporaine baignée de lumière"
          className="h-[120%] w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FBF8F3]/80 via-[#FBF8F3]/55 to-[#FBF8F3]" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-4 pt-36 pb-16 sm:px-6 lg:px-8"
      >
        <div className="grid items-center gap-14 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.7 }}
              className="mb-8 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-[#B36C45]"
              data-testid="hero-eyebrow"
            >
              <span className="inline-block h-px w-10 bg-[#C87D55]" />
              {t.hero.eyebrow}
            </motion.p>

            <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-[#0A1F18] sm:text-6xl lg:text-7xl">
              {t.hero.lines.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span className="block" {...lineAnim(i)}>
                    {i === 1 ? (
                      <>
                        <span className="font-serif italic font-medium text-[#C87D55]">
                          {line.split(" ").slice(0, 1)}
                        </span>{" "}
                        {line.split(" ").slice(1).join(" ")}
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8 }}
              className="mt-8 max-w-xl text-base leading-relaxed text-[#5C6B64] sm:text-lg"
              data-testid="hero-subtitle"
            >
              {t.hero.sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-services"
                onClick={() => scrollToId("expertises")}
                className="group flex items-center gap-3 bg-[#C87D55] px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#FBF8F3] transition-colors duration-300 hover:bg-[#0A1F18]"
              >
                {t.hero.cta1}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
              <button
                data-testid="hero-cta-contact"
                onClick={() => scrollToId("contact")}
                className="border border-[#0A1F18]/25 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#0A1F18] transition-colors duration-300 hover:border-[#C87D55] hover:text-[#B36C45]"
              >
                {t.hero.cta2}
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 1 }}
            className="hidden lg:block"
            style={{ perspective: 900 }}
          >
            <motion.div
              data-testid="hero-mandate-card"
              style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
              className="border border-[#E4DDD0] bg-white/80 p-8 shadow-xl shadow-[#0A1F18]/5 backdrop-blur-xl"
            >
              <div className="flex items-center justify-between border-b border-[#E4DDD0] pb-5">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B64]">
                  Columbus · GmbH
                </span>
                <span className="h-2 w-2 rotate-45 bg-[#C87D55]" />
              </div>
              <h3 className="mt-6 font-display text-2xl font-semibold text-[#0A1F18]">
                {t.hero.card.title}
              </h3>
              <p className="mt-2 text-sm text-[#5C6B64]">{t.hero.card.sub}</p>
              <div className="mt-8 space-y-0">
                {t.hero.card.items.map((item, i) => (
                  <div
                    key={item}
                    className="group flex items-center justify-between border-t border-[#E4DDD0] py-3.5"
                  >
                    <span className="flex items-center gap-4">
                      <span className="font-mono text-[10px] text-[#B36C45]">
                        0{i + 1}
                      </span>
                      <span className="text-sm text-[#0A1F18] transition-transform duration-300 group-hover:translate-x-1">
                        {item}
                      </span>
                    </span>
                    <ArrowUpRight className="h-3.5 w-3.5 text-[#B36C45] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="relative z-10 border-t border-[#E4DDD0]"
        data-testid="hero-stats-bar"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B64] sm:px-6 sm:text-[11px] lg:px-8">
          <span>{t.hero.coords}</span>
          <span className="hidden sm:inline">{t.hero.est}</span>
          <span className="hidden md:inline">{t.hero.reg}</span>
          <span className="flex items-center gap-2 text-[#B36C45]">
            {t.hero.scroll}
            <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
          </span>
        </div>
      </motion.div>
    </section>
  );
};
